/**
 * ValuesTab Component
 *
 * Pill-based attribute value assignment. Each attribute is a card with
 * inline pill selectors per scale. Two modes per card:
 *   - Score: clicking a pill immediately records an authoritative value
 *   - Estimate: clicking a pill proposes a soft estimate (multi-party; aggregated later)
 */

import { SessionValuesLine } from './SessionValuesLine';
import { useMemo, useCallback, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Check,
  X,
  Clock,
  AlertCircle,
  Loader2,
  Users,
  TrendingUp,
  ChevronDown,
  ChevronUp,
  Sparkles,
  MessageSquare,
} from 'lucide-react';
import { analyzeContestation } from '../../../../lib/llmContestationService';
import { useAiCapability } from '../../../../lib/ai/useAiCapability';
import type { ContestationAnalysis } from '../../../../lib/llmContestationService';
import { getCurrentWorkspaceId } from '../../../../lib/supabaseSync';
import { useValueAiSettingsStore } from '../../../../stores/prioritization/valueAiSettingsStore';
import { agentRunPersonWords, fetchValueDecisionRecords, type ValueDecisionRecord } from '../../../../lib/valueAiService';
import { agentProposerId, isAgentValueProposer } from '../../../../lib/valueEstimatorRunner';
import { useValueAgents, type AiEstimator } from '../../../../hooks/useValueAgents';
import { useValueProposalStore, type ValueProposal } from '../../../../stores/prioritization/valueProposalStore';
import { isOfficialValue } from '../../../../lib/valueScope';
import { valueStatus } from '../../../../lib/valueStatus';
import { aiCanJudgeValue, valueMarkFor } from '../../../../lib/valueMark';
import { ValueMark } from '@/components/ds';
import { describeEstimateSpread, type EstimateEntry } from '../../../../lib/estimateSpread';
import { useAttributesStore } from '../../../../stores/prioritization/attributesStore';
import { isObjectiveAttribute } from '@/lib/objectiveCriteria';
import { useUserStore } from '../../../../stores/prioritization/userStore';
import { useEntityCommentsStore } from '../../../../stores/prioritization/entityCommentsStore';
import { anchorToParam } from '../../../../lib/commentMentions';
import { knownActorName, useActorDisplayMap } from '../../../../hooks/prioritization/useActorDisplayMap';
import { ValueProvenanceLine } from '../../ValueProvenanceLine';
import { CriterionReadingHint } from '../../values/CriterionReadingHint';
import { Button } from '../../../ui/Button';
import { loadValueOutcomes, type ValueOutcome } from '../../../../lib/valueOutcomesService';
import type { AcceptedValue } from '../../../../types/prioritization/itemLifecycle';
import type { ValueKind, ValueAggregationMethod } from '../../../../types/prioritization/itemLifecycle';
import type { Attribute, MeasurementScale } from '../../../../types/prioritization/lifecycle';

// ============================================================================
// Types
// ============================================================================

export interface ValuesTabProps {
  itemId: string;
  canAccept?: boolean;
  readOnly?: boolean;
  className?: string;
  /** Show only this criterion's card, without the criterion tabs or the planning note (the item page's Assessment row). */
  attributeKey?: string;
}

// Semantic pill colors per spec
const PILL_STYLES = {
  low: 'bg-[#EAF3DE] border-[#3B6D11] text-[#27500A]',
  mid: 'bg-[#FAEEDA] border-[#854F0B] text-[#633806]',
  high: 'bg-[#FCEBEB] border-[#A32D2D] text-[#791F1F]',
  numeric: 'bg-[#E6F1FB] border-[#185FA5] text-[#0C447C]',
  default: 'bg-transparent border-granite-gray-300 text-granite-gray-600',
  estimate: 'bg-[#F3EDFC] border-[#7C3AED] text-[#5B21B6]',
} as const;

function getPillStyleForValue(
  label: string,
  scale: MeasurementScale
): keyof typeof PILL_STYLES {
  const l = label.toLowerCase().trim();

  if (scale.scaleType === 'numeric' || /^\d+$/.test(String(label))) {
    return 'numeric';
  }

  const lowLabels = ['low', 'xs', 's', 'insufficient', 'barely sufficient', 'none', 'n/a'];
  const midLabels = ['medium', 'm', 'sufficient'];
  const highLabels = ['high', 'l', 'xl', 'ideal'];

  if (lowLabels.includes(l)) return 'low';
  if (midLabels.includes(l)) return 'mid';
  if (highLabels.includes(l)) return 'high';

  return 'numeric';
}

// ============================================================================
// ValueKind Badge
// ============================================================================

function ValueKindBadge({ kind }: { kind?: ValueKind }) {
  if (kind === 'estimate') {
    return (
      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#F3EDFC] border border-[#7C3AED]/30 text-[#5B21B6]" title="This value was aggregated from estimates">
        <TrendingUp className="h-2.5 w-2.5" />
        aggregated estimate
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-granite-gray-100 border border-granite-gray-300 text-granite-gray-600" title="This is the authoritative accepted score">
      <Check className="h-2.5 w-2.5" />
      accepted score
    </span>
  );
}

// ============================================================================
// Side-by-side estimates (V-8): each person's estimate, and whether they are far apart
// ============================================================================

function EstimateSpreadLine({ entries, storedContested }: { entries: EstimateEntry[]; storedContested?: boolean | null }) {
  const spread = describeEstimateSpread(entries, storedContested);
  if (!spread) return null;
  return (
    <p className="text-[11px] text-[var(--text-sec)]" data-testid="estimate-spread">
      {spread.sideBySide} —{' '}
      <span className={spread.farApart ? 'font-semibold text-[var(--mustard)]' : 'text-[var(--text-ter)]'}>{spread.verdict}</span>
    </p>
  );
}

// ============================================================================
// Distribution Bar (for aggregated estimate accepted values)
// ============================================================================

interface DistributionBarProps {
  accepted: AcceptedValue;
  itemTitle?: string;
  attrLabel?: string;
  /** The person's name for a contributor id (side-by-side estimates). */
  nameFor: (proposedBy: string) => string;
}

function DistributionBar({ accepted, itemTitle, attrLabel, nameFor }: DistributionBarProps) {
  const contestationAnalyzerEnabled = useAiCapability('contestation_analyzer');
  const [contestAnalysis, setContestAnalysis] = useState<ContestationAnalysis | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const inputs = accepted.aggregationInputs ?? [];
  if (inputs.length < 2) return null;

  const numericVals = inputs.map((i) => Number(i.value)).filter((n) => !isNaN(n));
  const isNumeric = numericVals.length === inputs.length;
  const consensusNum = isNumeric ? Number(accepted.value) : NaN;
  const isContested = accepted.isContested ?? (accepted.stdDev !== undefined ? accepted.stdDev > 1.0 : false);
  const sideBySide = (
    <EstimateSpreadLine
      entries={inputs.map((inp) => ({ who: nameFor(inp.proposedBy ?? 'unknown'), value: inp.value }))}
      storedContested={accepted.isContested ?? (accepted.stdDev !== undefined ? accepted.stdDev > 1.0 : null)}
    />
  );

  const handleAnalyzeContestation = async () => {
    setIsAnalyzing(true);
    setContestAnalysis(null);
    const result = await analyzeContestation({
      attributeLabel: attrLabel ?? 'this attribute',
      itemTitle: itemTitle ?? 'this item',
      inputs: inputs.map((inp) => ({
        proposedBy: inp.proposedBy ?? 'unknown',
        value: inp.value,
        confidence: (inp as { confidence?: number }).confidence,
      })),
      consensusValue: accepted.value,
      stdDev: accepted.stdDev,
    });
    setContestAnalysis(result);
    setIsAnalyzing(false);
  };

  if (!isNumeric) {
    // Categorical: show value chip counts
    const counts = new Map<string, number>();
    inputs.forEach((i) => {
      const v = String(i.value);
      counts.set(v, (counts.get(v) ?? 0) + 1);
    });
    const chips = [...counts.entries()].sort((a, b) => b[1] - a[1]);
    return (
      <div className="mt-2 space-y-2">
        {sideBySide}
        <div className="flex flex-wrap gap-1.5 items-center">
          {chips.map(([val, count]) => (
            <span
              key={val}
              className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-medium bg-[var(--slate-dim)] border border-[var(--slate)] text-[var(--text-sec)]"
            >
              {count} {val}
            </span>
          ))}
          {isContested ? (
            <>
              <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[var(--mustard-dim)] border border-[var(--mustard)] text-[var(--mustard)]">
                <AlertCircle className="h-2.5 w-2.5" />
                Contested
              </span>
              {contestationAnalyzerEnabled && (
              <button
                onClick={handleAnalyzeContestation}
                disabled={isAnalyzing}
                className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-medium bg-[var(--slate-dim)] border border-[var(--slate)] text-[var(--text-sec)] hover:bg-[var(--slate-dim)] disabled:opacity-60 transition-colors"
                title="AI: Synthesize the disagreement"
              >
                <Sparkles className="h-2.5 w-2.5" />
                {isAnalyzing ? 'Analyzing…' : 'Explain'}
              </button>
              )}
            </>
          ) : (
            <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[var(--green-dim)] border border-[var(--green)] text-[var(--green)] dark:text-[var(--text-ter)]">
              <Check className="h-2.5 w-2.5" />
              Agreed
            </span>
          )}
        </div>
        {contestAnalysis && (
          <div className="p-2.5 bg-[var(--slate-dim)] border border-[var(--slate)] space-y-1.5">
            <div className="flex items-center gap-1.5">
              <Sparkles className="h-3 w-3 text-[var(--text-ter)]" />
              <span className="text-[10px] font-semibold text-[var(--text-sec)]">Disagreement Analysis</span>
              <button onClick={() => setContestAnalysis(null)} className="ml-auto text-[var(--text-sec)] hover:text-[var(--text-pri)]">
                <X className="h-3 w-3" />
              </button>
            </div>
            <p className="text-[10px] text-[var(--text-sec)]">{contestAnalysis.summary}</p>
            {contestAnalysis.positions.length > 0 && (
              <ul className="space-y-0.5">
                {contestAnalysis.positions.map((pos, i) => (
                  <li key={i} className="text-[10px] text-[var(--text-sec)]">• {pos}</li>
                ))}
              </ul>
            )}
            {contestAnalysis.crux && (
              <p className="text-[10px] text-[var(--mustard)] font-medium">Crux: {contestAnalysis.crux}</p>
            )}
            {contestAnalysis.suggestion && (
              <p className="text-[10px] text-[var(--text-sec)] italic">Try: {contestAnalysis.suggestion}</p>
            )}
          </div>
        )}
      </div>
    );
  }

  // Numeric: draw a range bar with contributor dots
  const minVal = Math.min(...numericVals);
  const maxVal = Math.max(...numericVals);
  const range = maxVal - minVal;

  const toPercent = (v: number) =>
    range === 0 ? 50 : Math.round(((v - minVal) / range) * 100);

  const consensusPct = !isNaN(consensusNum) ? toPercent(consensusNum) : 50;

  return (
    <div className="mt-2 space-y-1.5">
      {sideBySide}
      {/* Badges row */}
      <div className="flex items-center gap-1.5">
        {isContested ? (
          <>
            <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[var(--mustard-dim)] border border-[var(--mustard)] text-[var(--mustard)]">
              <AlertCircle className="h-2.5 w-2.5" />
              Contested
              {accepted.spread != null && (
                <span className="ml-0.5 opacity-70">spread {accepted.spread.toFixed(1)}</span>
              )}
            </span>
            {contestationAnalyzerEnabled && (
            <button
              onClick={handleAnalyzeContestation}
              disabled={isAnalyzing}
              className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-medium bg-[var(--slate-dim)] border border-[var(--slate)] text-[var(--text-sec)] hover:bg-[var(--slate-dim)] disabled:opacity-60 transition-colors"
              title="AI: Synthesize the disagreement"
            >
              <Sparkles className="h-2.5 w-2.5" />
              {isAnalyzing ? 'Analyzing…' : 'Explain'}
            </button>
            )}
          </>
        ) : (
          <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[var(--green-dim)] border border-[var(--green)] text-[var(--green)] dark:text-[var(--text-ter)]">
            <Check className="h-2.5 w-2.5" />
            Agreed
          </span>
        )}
        {accepted.stdDev != null && (
          <span className="text-[10px] text-granite-gray-400">
            σ {accepted.stdDev.toFixed(2)}
          </span>
        )}
      </div>
      {/* Range bar */}
      <div className="relative h-2 rounded-full bg-granite-gray-200 overflow-visible mx-2">
        {/* Filled extent */}
        <div
          className="absolute top-0 h-2 rounded-full bg-[var(--slate-dim)]"
          style={{ left: 0, right: 0 }}
        />
        {/* Contributor dots */}
        {inputs.map((inp, i) => {
          const pct = toPercent(Number(inp.value));
          return (
            <div
              key={i}
              title={`${nameFor(inp.proposedBy ?? 'unknown')}: ${inp.value}`}
              className="absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-[var(--slate)] border border-white shadow-sm cursor-default"
              style={{ left: `calc(${pct}% - 5px)` }}
            />
          );
        })}
        {/* Consensus marker */}
        <div
          className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-[var(--slate)] border-2 border-white shadow"
          style={{ left: `calc(${consensusPct}% - 6px)` }}
          title={`Consensus: ${accepted.value}`}
        />
      </div>
      {/* Min / max labels */}
      <div className="flex justify-between text-[10px] text-granite-gray-400 mx-2">
        <span>{minVal}</span>
        <span>{maxVal}</span>
      </div>
      {/* AI contestation analysis */}
      {contestAnalysis && (
        <div className="mt-2 p-2.5 bg-[var(--slate-dim)] border border-[var(--slate)] space-y-1.5">
          <div className="flex items-center gap-1.5">
            <Sparkles className="h-3 w-3 text-[var(--text-ter)]" />
            <span className="text-[10px] font-semibold text-[var(--text-sec)]">Disagreement Analysis</span>
            <button onClick={() => setContestAnalysis(null)} className="ml-auto text-[var(--text-sec)] hover:text-[var(--text-pri)]">
              <X className="h-3 w-3" />
            </button>
          </div>
          <p className="text-[10px] text-[var(--text-sec)]">{contestAnalysis.summary}</p>
          {contestAnalysis.positions.length > 0 && (
            <ul className="space-y-0.5">
              {contestAnalysis.positions.map((pos, i) => (
                <li key={i} className="text-[10px] text-[var(--text-sec)]">• {pos}</li>
              ))}
            </ul>
          )}
          {contestAnalysis.crux && (
            <p className="text-[10px] text-[var(--mustard)] font-medium">Crux: {contestAnalysis.crux}</p>
          )}
          {contestAnalysis.suggestion && (
            <p className="text-[10px] text-[var(--text-sec)] italic">Try: {contestAnalysis.suggestion}</p>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Aggregate Panel
// ============================================================================

interface AggregatePanelProps {
  proposals: ValueProposal[];
  onAggregate: (method: ValueAggregationMethod) => Promise<void>;
  onCancel: () => void;
  userId: string;
  userName: string;
  actorMap: Record<string, string>;
  /** Who an agent's proposal was made for ("for Dana", "run by Wes"), from its decision record. */
  onBehalfOf: (proposalId: string) => string | null;
}

function AggregatePanel({ proposals, onAggregate, onCancel, userId, userName, actorMap, onBehalfOf }: AggregatePanelProps) {
  const [method, setMethod] = useState<ValueAggregationMethod>('mean');
  const [loading, setLoading] = useState(false);

  const preview = useMemo(() => {
    const nums = proposals
      .map((p) => Number(p.proposedValue))
      .filter((n) => !isNaN(n));
    if (nums.length === 0) return null;
    if (method === 'mean') {
      return (nums.reduce((a, b) => a + b, 0) / nums.length).toFixed(2);
    }
    if (method === 'median') {
      const sorted = [...nums].sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      return sorted.length % 2 !== 0
        ? String(sorted[mid])
        : ((sorted[mid - 1] + sorted[mid]) / 2).toFixed(2);
    }
    if (method === 'mode') {
      const freq = new Map<number, number>();
      nums.forEach((n) => freq.set(n, (freq.get(n) ?? 0) + 1));
      const top = [...freq.entries()].sort((a, b) => b[1] - a[1])[0];
      return top ? String(top[0]) : null;
    }
    return null;
  }, [proposals, method]);

  const nonNumeric = proposals.filter((p) => isNaN(Number(p.proposedValue)));

  return (
    <div className="mt-3 p-3 bg-[var(--slate-dim)] border border-[var(--slate)] space-y-3">
      <div className="flex items-center gap-2">
        <Users className="h-3.5 w-3.5 text-[var(--text-ter)] shrink-0" />
        <span className="text-xs font-medium text-[var(--text-sec)]">
          Aggregate {proposals.length} estimates
        </span>
      </div>

      <div className="space-y-1">
        {proposals.map((p) => (
          <div key={p.id} className="flex items-center justify-between text-xs text-[var(--text-sec)]">
            <span className="truncate max-w-[160px] text-granite-gray-600">
              {resolveProposerName(p.proposedBy, p.proposedByName, userId, userName, actorMap, onBehalfOf(p.id))}
            </span>
            <span className="font-medium">{String(p.proposedValue)}</span>
          </div>
        ))}
      </div>

      {nonNumeric.length === 0 && (
        <div className="flex items-center gap-2">
          <select
            value={method}
            onChange={(e) => setMethod(e.target.value as ValueAggregationMethod)}
            className="text-xs border border-[var(--slate)] rounded px-2 py-1 bg-[var(--bg-raised)] text-[var(--text-ter)] focus:outline-none focus:ring-1 focus:ring-violet-400"
          >
            <option value="mean">Mean</option>
            <option value="median">Median</option>
            <option value="mode">Mode</option>
          </select>
          {preview != null && (
            <span className="text-xs text-[var(--text-sec)]">
              → <strong>{preview}</strong>
            </span>
          )}
        </div>
      )}

      {nonNumeric.length > 0 && (
        <p className="text-xs text-granite-gray-500 italic">
          Non-numeric estimates — will use most recent value.
        </p>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          disabled={loading}
          onClick={async () => {
            setLoading(true);
            try { await onAggregate(method); } finally { setLoading(false); }
          }}
          className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:opacity-50"
        >
          {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
          Accept consensus
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 text-xs font-medium text-granite-gray-600 hover:bg-granite-gray-100"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Attribute Card with Pills
// ============================================================================

/**
 * Resolves a proposer ID to a human-readable name.
 * Priority: current-user match → actor map lookup → stored proposedByName → truncated UUID.
 * An agent's proposal also names the person it was made for (onBehalf: "for Dana",
 * "run by Wes"), read from that proposal's decision record.
 */
function resolveProposerName(
  proposedBy: string,
  proposedByName: string | undefined | null,
  currentUserId: string,
  currentUserName: string,
  actorMap?: Record<string, string>,
  onBehalf?: string | null,
): string {
  if (proposedBy === currentUserId) return currentUserName || 'You';
  // An agent's proposal is named after the agent and marked as AI, with the person behind the run.
  if (proposedBy.startsWith('agent:')) return `${proposedByName?.trim() || 'Agent'} (AI)${onBehalf ? ` ${onBehalf}` : ''}`;
  return actorMap?.[proposedBy] ?? proposedByName?.trim() ?? proposedBy.slice(0, 8);
}

interface AttributeValueCardProps {
  attr: Attribute & { scales?: MeasurementScale[] };
  itemId: string;
  readOnly: boolean;
  canAccept: boolean;
  userId: string;
  userName: string;
  actorMap: Record<string, string>;
  getAcceptedValue: (itemId: string, attrKey: string, sessionId?: string, scaleId?: string | null) => AcceptedValue | undefined;
  /** All proposals for this item (including accepted), for provenance on accepted-from-proposal values */
  proposalsForItem: ValueProposal[];
  pendingProposals: ValueProposal[];
  onValueChange: (attrKey: string, scaleId: string | null, value: string | null, kind: ValueKind) => Promise<void>;
  onProposeEstimate: (attrKey: string, scaleId: string | null, value: string, confidence?: number, rationale?: string, forceKind?: ValueKind) => Promise<void>;
  onAcceptProposal: (proposalId: string) => void;
  onRejectProposal: (proposalId: string) => void;
  onAggregateEstimates: (attrKey: string, proposalIds: string[], method: ValueAggregationMethod) => Promise<void>;
  /**
   * Agents that may estimate this attribute — the Value Estimator and any custom
   * agent made on its behavior that is on for it (PRD-value-suggestion-agent §7).
   */
  aiEstimators?: AiEstimator[];
  /** Whether this person may accept or reject a proposal from this proposer (an agent's review roles; spec §2 G). */
  mayReview?: (proposedBy: string) => boolean;
  /** What reality said about this item's accepted values (loop two). */
  outcomes?: ValueOutcome[];
  /** Who an agent's proposal was made for ("for Dana", "run by Wes"), from its decision record. */
  onBehalfOf?: (proposalId: string) => string | null;
}


function AttributeValueCard({
  attr,
  itemId,
  readOnly,
  canAccept,
  userId,
  userName,
  actorMap,
  getAcceptedValue,
  proposalsForItem,
  pendingProposals,
  onValueChange,
  onProposeEstimate,
  onAcceptProposal,
  onRejectProposal,
  onAggregateEstimates,
  aiEstimators = [],
  mayReview = () => true,
  outcomes = [],
  onBehalfOf = () => null,
}: AttributeValueCardProps) {
  const [updatingKey, setUpdatingKey] = useState<string | null>(null);
  const [showAggregate, setShowAggregate] = useState(false);
  const [confidence, setConfidence] = useState<number>(80);
  const [rationale, setRationale] = useState('');

  const attrLabel = (attr as { label?: string }).label ?? attr.key;
  const scales: MeasurementScale[] = useMemo(() => attr.scales ?? [], [attr.scales]);

  // Who decided each official value (SPEC-value-mark-icon): one rule, lib/valueStatus + lib/valueMark.
  const allProposals = useValueProposalStore((s) => s.proposals);
  const replacements = useValueProposalStore((s) => s.replacements);
  const aiSetting = useValueAiSettingsStore((s) => s.settings.find((x) => x.attributeKey === attr.key) ?? null);
  const valueAgentName = useValueAiSettingsStore((s) => s.agentName);
  const markFor = (accepted: AcceptedValue | null | undefined) =>
    accepted
      ? valueMarkFor(
          valueStatus(itemId, attr.key, [accepted], allProposals, replacements),
          aiSetting,
          aiCanJudgeValue(itemId, attr.key, aiSetting, allProposals),
          { nameOf: (id) => (id ? actorMap[id] ?? null : null), agentName: valueAgentName },
        )
      : null;

  // Estimating agents: one click asks one agent for a proposal; the answer (or the abstain) shows here.
  const [aiBusy, setAiBusy] = useState<string | null>(null);
  const [aiMessage, setAiMessage] = useState<{ tone: 'ok' | 'warn' | 'error'; text: string } | null>(null);
  // An agent with a proposal already waiting here is not offered again. It is offered when a value
  // already stands (owner 2026-10-21): its answer waits beside the value until a person accepts it.
  const offeredEstimators = readOnly
    ? []
    : aiEstimators.filter((e) => !pendingProposals.some((p) => p.proposedBy === agentProposerId(e.agentType)));
  const handleAiEstimate = useCallback(async (estimator: AiEstimator) => {
    setAiBusy(estimator.agentType);
    setAiMessage(null);
    try {
      const outcome = await estimator.onEstimate(attr.key);
      if (outcome.outcome === 'proposed') setAiMessage({ tone: 'ok', text: `${estimator.name} proposed ${outcome.value} (${Math.round(outcome.confidence * 100)}% confidence). Review it below.` });
      else if (outcome.outcome === 'abstained') setAiMessage({ tone: 'warn', text: `${estimator.name} could not estimate: ${outcome.reason}` });
      else setAiMessage({ tone: outcome.outcome === 'error' ? 'error' : 'warn', text: outcome.reason ?? 'Not estimated.' });
    } finally {
      setAiBusy(null);
    }
  }, [attr.key]);
  const openThreads = useEntityCommentsStore(
    (s) => s.getThreadCounts('item', itemId, attr.key).open,
  );

  const estimateProposals = pendingProposals.filter((p) => p.valueKind === 'estimate');
  const scoreProposals = pendingProposals.filter((p) => p.valueKind !== 'estimate');
  const otherEstimates = estimateProposals.filter((p) => p.proposedBy !== userId);

  // Per-scale map of this user's pending estimate proposal
  const myEstimateByScale = useMemo(() => {
    const m = new Map<string, ValueProposal>();
    estimateProposals
      .filter((p) => p.proposedBy === userId && p.scaleId)
      .forEach((p) => m.set(p.scaleId!, p));
    return m;
  }, [estimateProposals, userId]);

  // Who (other than current user) first set the official score — used for the change-request note
  const firstAcceptedByOther = useMemo(() => {
    for (const scale of scales) {
      const av = getAcceptedValue(itemId, attr.key, undefined, scale.id);
      if (av?.acceptedBy && av.acceptedBy !== userId) return av;
    }
    return null;
  }, [scales, getAcceptedValue, itemId, attr.key, userId]);

  const scoredByName = firstAcceptedByOther
    ? (actorMap[firstAcceptedByOther.acceptedBy] || firstAcceptedByOther.acceptedBy?.slice(0, 8))
    : null;

  const handleScorePillClick = useCallback(
    async (scaleId: string | null, label: string, currentValue: string | null) => {
      if (readOnly || !canAccept) return;
      const key = `score::${attr.key}::${scaleId ?? ''}`;
      setUpdatingKey(key);
      try {
        const accepted = getAcceptedValue(itemId, attr.key, undefined, scaleId);
        const acceptedByOther = accepted?.acceptedBy && accepted.acceptedBy !== userId;
        if (acceptedByOther) {
          await onProposeEstimate(attr.key, scaleId, label, undefined, undefined, 'score');
        } else {
          const isDeselect = currentValue != null && currentValue === label;
          await onValueChange(attr.key, scaleId, isDeselect ? null : label, 'score');
        }
      } finally {
        setUpdatingKey(null);
      }
    },
    [attr.key, itemId, userId, readOnly, canAccept, getAcceptedValue, onValueChange, onProposeEstimate]
  );

  const handleEstimatePillClick = useCallback(
    async (scaleId: string | null, label: string) => {
      if (readOnly) return;
      const key = `estimate::${attr.key}::${scaleId ?? ''}`;
      setUpdatingKey(key);
      try {
        await onProposeEstimate(attr.key, scaleId, label, confidence / 100, rationale || undefined);
      } finally {
        setUpdatingKey(null);
      }
    },
    [attr.key, readOnly, confidence, rationale, onProposeEstimate]
  );

  return (
    <div
      className="rounded-[12px] border border-granite-gray-200 bg-granite-gray-50/30 p-4"
      style={{ borderWidth: '0.5px' }}
    >
      {/* Attribute name, what the criterion measures, and the discussion about this one value */}
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <div
            className="text-[13px] font-medium uppercase tracking-[0.06em] text-granite-gray-500"
            style={{ letterSpacing: '0.06em' }}
          >
            {attrLabel}
          </div>
          <CriterionReadingHint attributeKey={attr.key} />
        </div>
        <Link
          to={`/prioritization/items/${itemId}?tab=discussion&anchor=${anchorToParam({ type: 'attribute', key: attr.key, label: attrLabel })}`}
          className="inline-flex items-center gap-1 text-xs text-[var(--text-ter)] hover:text-[var(--text-pri)]"
          title={openThreads > 0 ? `${openThreads} open thread${openThreads === 1 ? '' : 's'} about ${attrLabel}` : `Discuss ${attrLabel}`}
        >
          <MessageSquare className="h-3.5 w-3.5" aria-hidden />
          {openThreads > 0 ? openThreads : 'Discuss'}
        </Link>
      </div>
      {offeredEstimators.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-2">
          {offeredEstimators.map((estimator) => {
            const busy = aiBusy === estimator.agentType;
            return (
              <Button
                key={estimator.agentType}
                variant="secondary"
                size="sm"
                onClick={() => void handleAiEstimate(estimator)}
                disabled={aiBusy !== null}
                title={`Ask ${estimator.name} for a proposed ${attrLabel.toLowerCase()} — you accept, change, or reject it`}
              >
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                {busy ? 'Asking…' : `Ask ${estimator.name}`}
              </Button>
            );
          })}
        </div>
      )}
      {aiMessage && (
        <p className={`mb-3 text-xs ${aiMessage.tone === 'ok' ? 'text-[var(--green)]' : aiMessage.tone === 'warn' ? 'text-[var(--mustard)]' : 'text-[var(--coral)]'}`}>
          {aiMessage.text}
        </p>
      )}

      {scales.length === 0 ? (
        <p className="text-sm italic text-granite-gray-500">
          No scales configured. <Link to="/prioritization/settings/attributes" className="text-primary-600 hover:underline">+ Add scale in settings</Link>
        </p>
      ) : (
        <>
          {/* ─── OFFICIAL SCORE ──────────────────────────────── */}
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <Check className="h-3 w-3 text-granite-gray-400" />
              <span className="text-[11px] font-semibold text-granite-gray-600 uppercase tracking-wide">Official Score</span>
              {canAccept && scoredByName ? (
                <span className="text-[10px] text-[var(--mustard)] font-normal normal-case tracking-normal">
                  · scored by {scoredByName} — clicking a different value submits a change request
                </span>
              ) : !canAccept ? (
                <span className="text-[10px] text-granite-gray-400 font-normal normal-case tracking-normal">
                  · set by an authorized scorer
                </span>
              ) : null}
            </div>

            <div className="space-y-0">
              {scales.map((scale, idx) => {
                const accepted = getAcceptedValue(itemId, attr.key, undefined, scale.id);
                const sourceProposal =
                  accepted?.acceptedFromProposalId != null
                    ? proposalsForItem.find((p) => p.id === accepted.acceptedFromProposalId)
                    : undefined;
                const currentValue = accepted?.value != null ? String(accepted.value) : null;
                const mark = currentValue ? markFor(accepted) : null;
                const scoreKey = `score::${attr.key}::${scale.id}`;
                const isUpdating = updatingKey === scoreKey;
                const hasPoints = scale.points && scale.points.length > 0;

                return (
                  <div key={scale.id}>
                    {idx > 0 && (
                      <div className="my-3 border-t border-granite-gray-200" style={{ borderWidth: '0.5px' }} />
                    )}
                    <div className="flex items-center gap-3 min-h-[36px]">
                      {scales.length > 1 && (
                        <span className="text-sm text-granite-gray-500 shrink-0" style={{ minWidth: 80 }}>
                          {scale.label}
                        </span>
                      )}
                      <div className="flex flex-wrap items-center gap-1.5 flex-1">
                        {hasPoints ? (
                          scale.points.map((pt) => {
                            const label = pt.label;
                            const isSelected = currentValue === label;
                            const styleKey = isSelected ? getPillStyleForValue(label, scale) : 'default';
                            const numericDisplay = !isNaN(Number(label)) ? null : pt.value;

                            return (
                              <button
                                key={pt.value}
                                type="button"
                                disabled={readOnly || !canAccept || isUpdating}
                                onClick={() => handleScorePillClick(scale.id, label, currentValue)}
                                className={`
                                  inline-flex items-center gap-1 px-3 py-1.5 rounded-[100px] text-sm font-medium
                                  transition-colors border
                                  ${isSelected ? PILL_STYLES[styleKey] : PILL_STYLES.default}
                                  ${canAccept && !readOnly && !isUpdating ? 'cursor-pointer hover:border-granite-gray-400 hover:bg-granite-gray-100' : 'cursor-default opacity-60'}
                                  disabled:opacity-50 disabled:cursor-not-allowed
                                `}
                                style={{ borderWidth: '0.5px' }}
                              >
                                {isUpdating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                                {numericDisplay !== null && (
                                  <span className="opacity-50 text-[11px] font-normal">{numericDisplay}·</span>
                                )}
                                {label}
                              </button>
                            );
                          })
                        ) : (
                          <span className="text-sm italic text-granite-gray-500">No options</span>
                        )}
                        {!currentValue && !canAccept && hasPoints && (
                          <span className="text-[11px] text-granite-gray-400 italic">Not yet scored</span>
                        )}
                        {mark && <ValueMark {...mark} className="ml-1" />}
                        {/* A visible way to take a value off (owner, 2026-10-15). Clicking the
                            chosen option again also clears it, but nothing said so. */}
                        {currentValue && canAccept && !readOnly && (
                          <button
                            type="button"
                            disabled={isUpdating}
                            onClick={async () => {
                              const key = `score::${attr.key}::${scale.id ?? ''}`;
                              setUpdatingKey(key);
                              try {
                                await onValueChange(attr.key, scale.id, null, 'score');
                              } finally {
                                setUpdatingKey(null);
                              }
                            }}
                            className="ml-1 font-mono text-[11px] uppercase tracking-[0.07em] text-[var(--text-ter)] underline decoration-[var(--rule-bright)] underline-offset-[3px] hover:text-[var(--text-pri)] disabled:opacity-50"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                    </div>

                    {accepted && (
                      <div className="mt-1.5">
                        <div className="flex items-center gap-2 flex-wrap">
                          <ValueProvenanceLine
                            acceptedBy={accepted.acceptedBy}
                            acceptedAt={accepted.acceptedAt}
                            acceptanceMethod={accepted.acceptanceMethod}
                            acceptanceNote={accepted.acceptanceNote}
                            proposedBy={
                              sourceProposal && isAgentValueProposer(sourceProposal.proposedBy)
                                ? resolveProposerName(sourceProposal.proposedBy, sourceProposal.proposedByName, userId, userName, actorMap, onBehalfOf(sourceProposal.id))
                                : sourceProposal?.proposedBy
                            }
                            proposedAt={sourceProposal?.proposedAt}
                            proposalNote={sourceProposal?.rationale}
                            compact
                          />
                          <ValueKindBadge kind={accepted.valueKind} />
                          {(() => {
                            const outcome = outcomes.find((o) => o.attributeKey === attr.key && (o.scaleId ?? null) === (scale.id ?? null));
                            return outcome ? (
                              <span
                                className="text-[10px] text-[var(--text-sec)]"
                                title={`Recorded from ${outcome.realizedSource === 'delivery' ? 'the delivery record' : outcome.realizedSource === 'connector' ? 'the connected system' : 'a person'} on ${new Date(outcome.realizedAt).toLocaleDateString()}`}
                              >
                                · Actual <strong>{String(outcome.realizedValue)}</strong>{outcome.error != null && outcome.error > 0 ? ` (off by ${outcome.error})` : outcome.error === 0 ? ' (exact)' : ''}
                              </span>
                            ) : null;
                          })()}
                          {accepted.contributorCount != null && accepted.contributorCount > 1 && (
                            <span className="inline-flex items-center gap-0.5 text-[10px] text-granite-gray-500">
                              <Users className="h-2.5 w-2.5" />
                              {accepted.contributorCount} contributors
                            </span>
                          )}
                        </div>
                        {accepted.valueKind === 'estimate' && (accepted.contributorCount ?? 0) >= 2 && (
                          <DistributionBar
                            accepted={accepted}
                            attrLabel={attrLabel}
                            nameFor={(id) => {
                              const p = proposalsForItem.find((x) => x.proposedBy === id);
                              return resolveProposerName(id, p?.proposedByName, userId, userName, actorMap, p ? onBehalfOf(p.id) : null);
                            }}
                          />
                        )}
                      </div>
                    )}
                    {/* Every session that valued this item on this criterion (canon 03 §2.7):
                        its number beside the record, and the record is one click away. */}
                    <SessionValuesLine
                      itemId={itemId}
                      attributeKey={attr.key}
                      officialValue={currentValue}
                      canAccept={canAccept && !readOnly}
                      allowedLabels={hasPoints ? scale.points!.map((pt) => String(pt.value)) : undefined}
                      onMakeRecord={(value) => onValueChange(attr.key, scale.id, value, 'score')}
                    />
                  </div>
                );
              })}
            </div>

            {/* Pending re-score proposals */}
            {scoreProposals.length > 0 && (
              <div className="mt-3 pt-3 border-t border-granite-gray-200 space-y-2" style={{ borderTopWidth: '0.5px' }}>
                <div className="space-y-0.5">
                  <div className="text-xs font-medium text-[var(--mustard)] flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Proposed re-scores ({scoreProposals.length})
                  </div>
                  <p className="text-[10px] text-granite-gray-400 leading-snug">
                    {canAccept
                      ? 'Someone disagreed with the current score. Accept to replace it, or reject to dismiss.'
                      : 'Someone proposed a different score. A facilitator needs to accept or reject it.'}
                  </p>
                </div>
                {scoreProposals.map((p) => (
                  <div
                    key={p.id}
                    className="flex items-center justify-between bg-[var(--mustard-dim)]/80 border border-[var(--mustard)] px-3 py-2"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm font-medium text-[var(--text-pri)]">{String(p.proposedValue)}</span>
                      <span className="text-[10px] text-granite-gray-400 truncate">
                        by {resolveProposerName(p.proposedBy, p.proposedByName, userId, userName, actorMap, onBehalfOf(p.id))}
                        {p.sessionId ? ' · contributed in a session' : ''}
                      </span>
                    </div>
                    {canAccept && mayReview(p.proposedBy) && (
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-[10px] text-granite-gray-400 hidden sm:block">Replace current score?</span>
                        <button
                          onClick={() => onAcceptProposal(p.id)}
                          className="p-1 text-[var(--green)] dark:text-[var(--text-ter)] hover:bg-[var(--green-dim)] rounded"
                          title="Accept — this becomes the official score and replaces the current one"
                        >
                          <Check className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => onRejectProposal(p.id)}
                          className="p-1 text-[var(--coral)] hover:bg-[var(--coral-dim)] rounded"
                          title="Reject — keep the current score"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ─── YOUR ESTIMATE ───────────────────────────────── */}
          {!readOnly && (
            <div className="mt-4 pt-4 border-t border-granite-gray-200" style={{ borderTopWidth: '0.5px' }}>
              <div className="flex items-center gap-1.5 mb-2">
                <TrendingUp className="h-3 w-3 text-[var(--text-ter)]" />
                <span className="text-[11px] font-semibold text-[var(--text-ter)] uppercase tracking-wide">Your Estimate</span>
                <span className="text-[10px] text-[var(--text-ter)] font-normal normal-case tracking-normal">
                  · your read on this, for discussion
                </span>
              </div>

              <div className="space-y-0">
                {scales.map((scale, idx) => {
                  const myEstimate = myEstimateByScale.get(scale.id);
                  const myEstimateValue = myEstimate ? String(myEstimate.proposedValue) : null;
                  const estimateKey = `estimate::${attr.key}::${scale.id}`;
                  const isUpdating = updatingKey === estimateKey;
                  const hasPoints = scale.points && scale.points.length > 0;

                  return (
                    <div key={scale.id}>
                      {idx > 0 && (
                        <div className="my-3 border-t border-granite-gray-200" style={{ borderWidth: '0.5px' }} />
                      )}
                      <div className="flex items-center gap-3 min-h-[36px]">
                        {scales.length > 1 && (
                          <span className="text-sm text-granite-gray-500 shrink-0" style={{ minWidth: 80 }}>
                            {scale.label}
                          </span>
                        )}
                        <div className="flex flex-wrap items-center gap-1.5 flex-1">
                          {hasPoints ? (
                            scale.points.map((pt) => {
                              const label = pt.label;
                              const isMyEstimate = myEstimateValue === label;
                              const numericDisplay = !isNaN(Number(label)) ? null : pt.value;

                              return (
                                <button
                                  key={pt.value}
                                  type="button"
                                  disabled={isUpdating}
                                  onClick={() => handleEstimatePillClick(scale.id, label)}
                                  className={`
                                    inline-flex items-center gap-1 px-3 py-1.5 rounded-[100px] text-sm font-medium
                                    transition-colors border cursor-pointer
                                    ${isMyEstimate ? PILL_STYLES.estimate : PILL_STYLES.default}
                                    ${!isMyEstimate ? 'hover:border-[var(--slate)] hover:bg-[var(--slate-dim)] hover:text-[var(--text-ter)]' : ''}
                                    disabled:opacity-50 disabled:cursor-not-allowed
                                  `}
                                  style={{ borderWidth: '0.5px' }}
                                >
                                  {isUpdating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                                  {numericDisplay !== null && (
                                    <span className="opacity-50 text-[11px] font-normal">{numericDisplay}·</span>
                                  )}
                                  {label}
                                </button>
                              );
                            })
                          ) : (
                            <span className="text-sm italic text-granite-gray-500">No options</span>
                          )}
                        </div>
                      </div>
                      {myEstimate && (
                        <div className="mt-1 flex items-center gap-1.5">
                          <Clock className="h-3 w-3 text-[var(--text-ter)] shrink-0" />
                          <span className="text-[10px] text-[var(--text-ter)]">
                            Your estimate: <strong>{String(myEstimate.proposedValue)}</strong> · pending review
                          </span>
                          {myEstimate.confidence != null && (
                            <span className="text-[10px] text-[var(--text-ter)]">
                              · {Math.round(myEstimate.confidence * 100)}% confidence
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Confidence + rationale for submitting estimates */}
              <div className="mt-3 space-y-2">
                <div className="flex items-center gap-3">
                  <label className="text-[10px] font-medium text-[var(--text-ter)] shrink-0">Confidence</label>
                  <input
                    type="range"
                    min={10}
                    max={100}
                    step={10}
                    value={confidence}
                    onChange={(e) => setConfidence(Number(e.target.value))}
                    className="flex-1 h-1.5 rounded-full appearance-none cursor-pointer accent-violet-600"
                  />
                  <span className="text-[10px] font-semibold text-[var(--text-ter)] w-8 text-right">{confidence}%</span>
                </div>
                <textarea
                  value={rationale}
                  onChange={(e) => setRationale(e.target.value)}
                  placeholder="Rationale (optional)"
                  rows={2}
                  className="w-full text-xs border border-[var(--slate)] px-2.5 py-1.5 bg-[var(--bg-raised)] text-granite-gray-700 focus:outline-none focus:ring-1 focus:ring-violet-400 resize-none"
                />
              </div>

              {/* Other contributors' estimates */}
              {otherEstimates.length > 0 && (
                <div className="mt-3 pt-3 border-t border-[var(--slate)] space-y-2" style={{ borderTopWidth: '0.5px' }}>
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] font-medium text-[var(--text-ter)] flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      Other estimates ({otherEstimates.length})
                    </div>
                    {canAccept && estimateProposals.length >= 2 && (
                      <button
                        type="button"
                        onClick={() => setShowAggregate((v) => !v)}
                        className="flex items-center gap-0.5 text-[11px] font-medium text-[var(--text-ter)] hover:text-[var(--text-ter)]"
                      >
                        Aggregate
                        {showAggregate ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                      </button>
                    )}
                  </div>
                  {estimateProposals.length >= 2 && (
                    <EstimateSpreadLine
                      entries={estimateProposals.map((p) => ({
                        who: resolveProposerName(p.proposedBy, p.proposedByName, userId, userName, actorMap, onBehalfOf(p.id)),
                        value: p.proposedValue,
                      }))}
                    />
                  )}
                  {otherEstimates.map((p) => (
                    <div
                      key={p.id}
                      className="bg-[var(--slate-dim)]/60 border border-[var(--slate)] px-3 py-2 space-y-1"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-sm text-[var(--text-pri)]">{String(p.proposedValue)}</span>
                          <span className="text-[10px] text-granite-gray-400 truncate">
                            {resolveProposerName(p.proposedBy, p.proposedByName, userId, userName, actorMap, onBehalfOf(p.id))}
                          </span>
                          {isAgentValueProposer(p.proposedBy) && (
                            <span
                              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-[var(--slate-dim)] text-[var(--text-sec)] border border-[var(--slate)]"
                              title="AI-generated estimate, awaiting your review"
                            >
                              <Sparkles className="h-3 w-3" /> AI
                            </span>
                          )}
                          {p.confidence != null && (
                            <span className="text-[10px] text-[var(--text-ter)] font-medium">{Math.round(p.confidence * 100)}% conf.</span>
                          )}
                        </div>
                        {canAccept && mayReview(p.proposedBy) && (
                          <div className="flex gap-1 shrink-0">
                            <button
                              onClick={() => onAcceptProposal(p.id)}
                              className="p-1 text-[var(--green)] dark:text-[var(--text-ter)] hover:bg-[var(--green-dim)] rounded"
                              title="Accept this estimate as score"
                            >
                              <Check className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => onRejectProposal(p.id)}
                              className="p-1 text-[var(--coral)] hover:bg-[var(--coral-dim)] rounded"
                              title="Reject"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </div>
                      {p.rationale && (
                        <p className="text-[10px] text-[var(--text-ter)] italic leading-snug pl-1">"{p.rationale}"</p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Aggregate panel — shown when canAccept and 2+ estimates exist */}
              {canAccept && estimateProposals.length >= 2 && otherEstimates.length === 0 && (
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={() => setShowAggregate((v) => !v)}
                    className="flex items-center gap-0.5 text-[11px] font-medium text-[var(--text-ter)] hover:text-[var(--text-ter)]"
                  >
                    Aggregate {estimateProposals.length} estimates
                    {showAggregate ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                  </button>
                </div>
              )}
              {showAggregate && (
                <AggregatePanel
                  proposals={estimateProposals}
                  userId={userId}
                  userName={userName}
                  actorMap={actorMap}
                  onBehalfOf={onBehalfOf}
                  onAggregate={async (method) => {
                    await onAggregateEstimates(
                      attr.key,
                      estimateProposals.map((p) => p.id),
                      method
                    );
                    setShowAggregate(false);
                  }}
                  onCancel={() => setShowAggregate(false)}
                />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ============================================================================
// ValuesTab
// ============================================================================

export function ValuesTab({
  itemId,
  canAccept = false,
  readOnly = false,
  className = '',
  attributeKey,
}: ValuesTabProps) {
  const fetchCommentsForEntity = useEntityCommentsStore((s) => s.fetchForEntity);
  useEffect(() => {
    if (itemId) void fetchCommentsForEntity('item', itemId);
  }, [itemId, fetchCommentsForEntity]);
  const userId = useUserStore((s) => s.userId) || '';
  const userName = useUserStore((s) => s.userName) || '';
  const currentRole = useUserStore((s) => s.currentRole) || 'workspace-member';
  const canEdit = !readOnly && canAccept;

  const attributes = useAttributesStore((s) => s.attributes);
  const proposals = useValueProposalStore((s) => s.proposals);
  const acceptedValues = useValueProposalStore((s) => s.acceptedValues);

  // Resolve proposer and acceptor UUIDs → display names for this item's proposals and accepted values
  const proposalsForItem = useMemo(
    () => proposals.filter((p) => p.itemId === itemId),
    [proposals, itemId]
  );

  // Who each agent proposal was made for: its own decision record (sponsor on an
  // automatic run, the person on a manual one), never the agent's settings today.
  const agentProposalKey = useMemo(
    () => proposalsForItem.filter((p) => isAgentValueProposer(p.proposedBy)).map((p) => p.id).sort().join(','),
    [proposalsForItem],
  );
  const [recordByProposal, setRecordByProposal] = useState<Record<string, ValueDecisionRecord>>({});
  useEffect(() => {
    const workspaceId = getCurrentWorkspaceId();
    if (!workspaceId || !agentProposalKey) {
      setRecordByProposal({});
      return;
    }
    let live = true;
    fetchValueDecisionRecords(workspaceId, itemId, null, 200)
      .then((records) => {
        if (!live) return;
        const byProposal: Record<string, ValueDecisionRecord> = {};
        // Newest first: the first record naming a proposal is the one that wrote it.
        records.forEach((r) => {
          if (r.proposalId && !byProposal[r.proposalId]) byProposal[r.proposalId] = r;
        });
        setRecordByProposal(byProposal);
      })
      .catch(() => live && setRecordByProposal({}));
    return () => {
      live = false;
    };
  }, [itemId, agentProposalKey]);

  const proposerIds = useMemo(() => {
    const ids = new Set<string>();
    proposalsForItem.forEach((p) => ids.add(p.proposedBy));
    acceptedValues.filter((av) => av.itemId === itemId && av.acceptedBy).forEach((av) => ids.add(av.acceptedBy!));
    Object.values(recordByProposal).forEach((r) => {
      if (r.sponsorUserId) ids.add(r.sponsorUserId);
      if (r.runByUserId) ids.add(r.runByUserId);
    });
    return [...ids];
  }, [proposalsForItem, acceptedValues, itemId, recordByProposal]);
  const actorMap = useActorDisplayMap(proposerIds);
  const onBehalfOf = useCallback(
    (proposalId: string) =>
      agentRunPersonWords(recordByProposal[proposalId], (id) => knownActorName(actorMap, id) ?? (id && id === userId ? userName || null : null)),
    [recordByProposal, actorMap, userId, userName],
  );
  const getAcceptedValue = useValueProposalStore((s) => s.getAcceptedValue);
  const {
    acceptValueDirectly,
    clearAcceptedValue,
    proposeValue,
    aggregateProposals,
    fetchFromSupabase,
  } = useValueProposalStore();

  // Fetch fresh data from DB whenever the item changes — §2.1 reconstructability.
  useEffect(() => {
    fetchFromSupabase();
  }, [itemId]); // eslint-disable-line react-hooks/exhaustive-deps

  const [activeAttrKey, setActiveAttrKey] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [rejectError, setRejectError] = useState<string | null>(null);
  // Inline reject confirmation — avoids window.confirm()
  const [pendingRejectId, setPendingRejectId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  // Default to first attribute
  const activeKey = attributeKey ?? activeAttrKey ?? attributes[0]?.key ?? null;

  // The agent that works on each value, and the acts on it — one copy, shared with the closed row.
  const { hiddenProposers, mayReview, estimatorsFor, acceptRecommendation, rejectRecommendation } = useValueAgents(itemId);
  const pendingByAttr = useMemo(() => {
    const m = new Map<string, ValueProposal[]>();
    proposals.forEach((p) => {
      if (p.itemId === itemId && p.status === 'proposed' && !hiddenProposers.has(p.proposedBy)) {
        const list = m.get(p.attributeKey) ?? [];
        list.push(p);
        m.set(p.attributeKey, list);
      }
    });
    m.forEach((list) => {
      list.sort((a, b) => new Date(b.proposedAt).getTime() - new Date(a.proposedAt).getTime());
    });
    return m;
  }, [proposals, itemId, hiddenProposers]);

  // For each attribute, check if it has an official value (for tab indicator dot).
  // A session's value is not the item's value (roadmap 1C, V-3).
  const hasValueByAttr = useMemo(() => {
    const m = new Map<string, boolean>();
    attributes.forEach((attr) => {
      m.set(attr.key, acceptedValues.some((av) => av.itemId === itemId && av.attributeKey === attr.key && isOfficialValue(av)));
    });
    return m;
  }, [attributes, acceptedValues, itemId]);

  const handleValueChange = useCallback(
    async (attributeKey: string, scaleId: string | null, value: string | null, kind: ValueKind) => {
      if (!userId) return;
      setSaveError(null);
      try {
        if (value == null) {
          const result = await clearAcceptedValue({ itemId, attributeKey, scaleId });
          if (!result.success && result.error) setSaveError(result.error);
        } else {
          const result = await acceptValueDirectly({
            itemId,
            attributeKey,
            value,
            acceptedBy: userId,
            acceptanceMethod: 'facilitator_override',
            acceptanceNote: 'Value set from Item detail Values tab',
            scaleId: scaleId ?? undefined,
            valueKind: kind,
          });
          if (result?.saved === false && result?.error) setSaveError(result.error);
        }
        await fetchFromSupabase();
      } catch (e) {
        setSaveError(e instanceof Error ? e.message : 'Failed to save');
      }
    },
    [itemId, userId, acceptValueDirectly, clearAcceptedValue, fetchFromSupabase]
  );

  const handleProposeEstimate = useCallback(
    async (attributeKey: string, scaleId: string | null, value: string, confidence?: number, rationale?: string, forceKind?: ValueKind) => {
      if (!userId) return;
      setSaveError(null);
      try {
        await proposeValue({
          itemId,
          attributeKey,
          scaleId,
          proposedValue: value,
          proposedBy: userId,
          proposedByName: userName,
          proposerRole: currentRole,
          confidence,
          rationale,
          valueKind: forceKind ?? 'estimate',
        });
        await fetchFromSupabase();
      } catch (e) {
        setSaveError(e instanceof Error ? e.message : 'Failed to propose estimate');
      }
    },
    [itemId, userId, userName, currentRole, proposeValue, fetchFromSupabase]
  );

  const handleAccept = useCallback(
    async (proposalId: string) => {
      setSaveError(null);
      setRejectError(null);
      try {
        await acceptRecommendation(proposalId);
      } catch (e) {
        setSaveError(e instanceof Error ? e.message : 'Accept failed');
      }
    },
    [acceptRecommendation]
  );

  const handleReject = useCallback(
    (proposalId: string) => {
      setPendingRejectId(proposalId);
      setRejectReason('');
      setRejectError(null);
    },
    []
  );

  const confirmReject = useCallback(
    async () => {
      if (!pendingRejectId) return;
      setRejectError(null);
      try {
        await rejectRecommendation(pendingRejectId, rejectReason);
        setPendingRejectId(null);
        setRejectReason('');
      } catch (e) {
        setRejectError(e instanceof Error ? e.message : 'Reject failed');
      }
    },
    [pendingRejectId, rejectReason, rejectRecommendation]
  );

  const [outcomes, setOutcomes] = useState<ValueOutcome[]>([]);
  useEffect(() => {
    const ws = getCurrentWorkspaceId();
    if (!ws || !itemId) return;
    void loadValueOutcomes({ workspaceId: ws, itemId }).then(setOutcomes);
  }, [itemId]);
  const pendingRejectIsAi = useMemo(
    () => !!pendingRejectId && isAgentValueProposer(proposalsForItem.find((p) => p.id === pendingRejectId)?.proposedBy),
    [pendingRejectId, proposalsForItem],
  );

  const handleAggregateEstimates = useCallback(
    async (_attributeKey: string, proposalIds: string[], method: ValueAggregationMethod) => {
      if (!userId) return;
      setSaveError(null);
      try {
        const result = await aggregateProposals({
          proposalIds,
          aggregationMethod: method,
          acceptedBy: userId,
          acceptorRole: currentRole,
        });
        if (!result.saved && result.error) setSaveError(result.error);
        await fetchFromSupabase();
      } catch (e) {
        setSaveError(e instanceof Error ? e.message : 'Aggregation failed');
      }
    },
    [userId, currentRole, aggregateProposals, fetchFromSupabase]
  );

  if (attributes.length === 0) {
    return (
      <div className={`${className} text-center py-8 text-granite-gray-500`}>
        <AlertCircle className="h-8 w-8 mx-auto mb-2 text-granite-gray-300" />
        <p className="text-sm">No attributes configured. Add attributes in Settings → Attributes.</p>
      </div>
    );
  }

  const activeAttr = attributes.find((a) => a.key === activeKey);

  return (
    <div className={className}>
      {saveError && (
        <div className="mb-3 p-3 bg-[var(--coral-dim)] border border-[var(--coral)] text-[var(--coral)] text-sm flex items-start gap-2">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>{saveError}</span>
          <button
            type="button"
            onClick={() => setSaveError(null)}
            className="ml-auto shrink-0 text-[var(--coral)] hover:text-[var(--coral)]"
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}
      {/* Inline rejection confirmation dialog */}
      {pendingRejectId && (
        <div className="mb-3 p-4 bg-[var(--coral-dim)] border border-[var(--coral)] space-y-3">
          <p className="text-sm font-medium text-[var(--coral)]">{pendingRejectIsAi ? 'Reject this AI estimate?' : 'Reject this proposal?'}</p>
          <textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder={pendingRejectIsAi ? 'Why is it wrong? (required — it teaches the next estimate)' : 'Reason for rejection (optional)'}
            rows={2}
            className="w-full px-3 py-2 text-sm border border-[var(--coral)] resize-none focus:outline-none focus:ring-2 focus:ring-red-300 bg-[var(--bg-raised)]"
          />
          {rejectError && <p className="text-xs text-[var(--coral)]">{rejectError}</p>}
          <div className="flex items-center gap-2 justify-end">
            <button
              type="button"
              onClick={() => { setPendingRejectId(null); setRejectReason(''); setRejectError(null); }}
              className="px-3 py-1.5 text-xs text-[var(--text-sec)] hover:bg-[var(--coral-dim)] transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmReject}
              disabled={pendingRejectIsAi && !rejectReason.trim()}
              className="px-3 py-1.5 text-xs bg-[var(--coral)] text-white font-medium hover:bg-[var(--coral)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Confirm rejection
            </button>
          </div>
        </div>
      )}

      {!attributeKey && (
      <>
      {/* Directional Planning context note */}
      <div className="mb-3 flex items-start gap-2 bg-primary-50 border border-primary-200 px-3 py-2">
        <span className="mt-0.5 shrink-0 text-primary-600">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" className="mt-0.5"><circle cx="6" cy="6" r="6"/><rect x="5.25" y="4" width="1.5" height="4" rx="0.75"/><rect x="5.25" y="2.5" width="1.5" height="1.5" rx="0.75"/></svg>
        </span>
        <p className="text-[11px] text-primary-700 leading-snug">
          <span className="font-semibold">Directional Planning.</span>{' '}
          These signals inform prioritization — they do not commit to execution. Use rough estimates, ranges, and confidence levels. Detailed Planning happens after priorities are accepted.
        </p>
      </div>

      {/* Horizontal attribute tab bar */}
      <div className="flex gap-0 border-b border-granite-gray-200 overflow-x-auto mb-4">
        {attributes.filter((attr) => !isObjectiveAttribute(attr) || hasValueByAttr.get(attr.key)).map((attr) => {
          const label = (attr as { label?: string }).label ?? attr.key;
          const isActive = attr.key === activeKey;
          const hasValue = hasValueByAttr.get(attr.key);
          const hasPending = (pendingByAttr.get(attr.key)?.length ?? 0) > 0;

          return (
            <button
              key={attr.key}
              type="button"
              onClick={() => setActiveAttrKey(attr.key)}
              className={`
                relative flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium whitespace-nowrap
                border-b-2 transition-colors focus:outline-none
                ${isActive
                  ? 'text-[var(--text-pri)] border-[var(--rule)] dark:border-[var(--text-pri)]'
                  : 'text-granite-gray-500 border-transparent hover:text-[var(--text-sec)] hover:border-granite-gray-300 dark:hover:border-[var(--rule-bright)]'
                }
              `}
            >
              {label}
              {/* Value set indicator */}
              {hasValue && !isActive && (
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--green)] shrink-0" title="Value set" />
              )}
              {/* Pending proposals badge */}
              {hasPending && (
                <span className={`inline-flex items-center justify-center min-w-[1.125rem] h-[1.125rem] px-1 text-[10px] font-medium rounded-full ${isActive ? 'bg-[var(--hover-tint)] text-[var(--text-sec)]' : 'bg-[var(--mustard-dim)] text-[var(--mustard)]'}`}>
                  {pendingByAttr.get(attr.key)!.length}
                </span>
              )}
            </button>
          );
        })}
      </div>

      </>
      )}

      {/* Active attribute card */}
      {activeAttr && (
        <AttributeValueCard
          attr={activeAttr as Attribute & { scales?: MeasurementScale[] }}
          itemId={itemId}
          readOnly={readOnly}
          canAccept={canEdit}
          userId={userId}
          userName={userName}
          actorMap={actorMap}
          getAcceptedValue={getAcceptedValue}
          proposalsForItem={proposalsForItem}
          pendingProposals={pendingByAttr.get(activeAttr.key) ?? []}
          onValueChange={handleValueChange}
          onProposeEstimate={handleProposeEstimate}
          onAcceptProposal={handleAccept}
          onRejectProposal={handleReject}
          onAggregateEstimates={handleAggregateEstimates}
          aiEstimators={estimatorsFor(activeAttr.key)}
          mayReview={mayReview}
          outcomes={outcomes}
          onBehalfOf={onBehalfOf}
        />
      )}
    </div>
  );
}

export default ValuesTab;
